import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../../../shared/components/PageHeader';
import { useLanguage } from '../../../app/i18n/LanguageContext';
import EmptyState from '../../../shared/components/EmptyState';
import LoadingState from '../../../shared/components/LoadingState';
import Modal from '../../../shared/components/Modal';
import { useCompanyWriteAccess } from '../../../shared/auth/useCompanyWriteAccess';
import './TDHPPage.css';

const baseEnvironment = process.env.REACT_APP_API_BASE_URL?.trim() || 'http://localhost:8010';
const API_BASE_URL = baseEnvironment.replace(/\/$/, '');

const UI_TEXT = {
  'tr-TR': {
    title: 'TDHP (Tek Duzen Hesap Plani)',
    description: "Sirket bazinda Turk Tek Duzen Hesap Plani'ni yukleyin ve hesaplari izleyin.",
    replacePlan: 'Mevcut plani silip yeniden yukle',
    addAccount: 'Hesap Ekle',
    loadPlan: 'TDHP Planini Yukle',
    loading: 'Yukleniyor...',
    company: 'Sirket',
    companyNotFound: 'Sirket bulunamadi',
    searchAccount: 'Hesap ara',
    searchPlaceholder: 'Kod veya isim',
    search: 'Ara',
    code: 'Kod',
    name: 'Ad',
    type: 'Tur',
    kind: 'Cins',
    balanceNature: 'Bakiye Dogasi',
    postable: 'Postable',
    status: 'Durum',
    actions: 'Islemler',
    selectCompanyPrompt: 'Sirket secimi yapin.',
    noAccounts: 'Henuz hesap bulunamadi.',
    level: 'Seviye',
    yes: 'Evet',
    no: 'Hayir',
    active: 'Aktif',
    passive: 'Pasif',
    edit: 'Duzenle',
    delete: 'Sil',
    editAccount: 'Hesabi Duzenle',
    newAccount: 'Yeni Hesap Ekle',
    close: 'Kapat',
    levelNo: 'Seviye No',
    currency: 'Para Birimi',
    parentCode: 'Ana Hesap Kodu',
    parentId: 'Ana Hesap ID',
    hasChildren: 'Alt Hesaba Sahip',
    cancel: 'Iptal',
    saving: 'Kaydediliyor...',
    update: 'Guncelle',
    save: 'Kaydet',
    companiesLoadError: 'Sirketler yuklenemedi.',
    companiesLoadErrorDetail: 'Sirketler yuklenirken hata olustu.',
    accountsLoadError: 'Hesaplar yuklenemedi.',
    accountsLoadErrorDetail: 'Hesaplar yuklenirken hata olustu.',
    selectCompanyFirst: 'Once sirket secmelisiniz.',
    seedLoadError: 'TDHP plani yuklenemedi.',
    seedLoadErrorDetail: 'TDHP plani yuklenirken hata olustu.',
    totalRecordsMessage: 'Toplam {{count}} kayit.',
    accountUpdateError: 'Hesap guncellenemedi.',
    accountCreateError: 'Hesap olusturulamadi.',
    accountUpdated: 'Hesap guncellendi.',
    accountCreated: 'Yeni hesap eklendi.',
    accountUpdateErrorDetail: 'Hesap guncellenirken hata olustu.',
    accountCreateErrorDetail: 'Hesap olusturulurken hata olustu.',
    deleteConfirm: 'Bu hesabi silmek istediginizden emin misiniz?',
    accountDeleteError: 'Hesap silinemedi.',
    accountDeleted: 'Hesap silindi.',
    accountDeleteErrorDetail: 'Hesap silinirken hata olustu.',
    accountTypeActive: 'Aktif',
    accountTypePassive: 'Pasif',
    accountTypeIncome: 'Gelir',
    accountTypeExpense: 'Gider',
    accountTypeNa: 'NA',
    accountKindMonetary: 'Parasal',
    accountKindMemorandum: 'Nazim',
    accountKindOther: 'Diger',
    balanceDebit: 'Borc',
    balanceCredit: 'Alacak',
    readOnlyAccess: 'Bu sirket icin yazma yetkiniz yok.'
  },
  'en-US': {
    title: 'TDHP (Uniform Chart of Accounts)',
    description: 'Load and manage the Turkish Uniform Chart of Accounts per company.',
    replacePlan: 'Delete existing plan and reload',
    addAccount: 'Add Account',
    loadPlan: 'Load TDHP Plan',
    loading: 'Loading...',
    company: 'Company',
    companyNotFound: 'No company found',
    searchAccount: 'Search account',
    searchPlaceholder: 'Code or name',
    search: 'Search',
    code: 'Code',
    name: 'Name',
    type: 'Type',
    kind: 'Kind',
    balanceNature: 'Balance Nature',
    postable: 'Postable',
    status: 'Status',
    actions: 'Actions',
    selectCompanyPrompt: 'Please select a company.',
    noAccounts: 'No accounts found yet.',
    level: 'Level',
    yes: 'Yes',
    no: 'No',
    active: 'Active',
    passive: 'Passive',
    edit: 'Edit',
    delete: 'Delete',
    editAccount: 'Edit Account',
    newAccount: 'Add New Account',
    close: 'Close',
    levelNo: 'Level No',
    currency: 'Currency',
    parentCode: 'Parent Account Code',
    parentId: 'Parent Account ID',
    hasChildren: 'Has Child Accounts',
    cancel: 'Cancel',
    saving: 'Saving...',
    update: 'Update',
    save: 'Save',
    companiesLoadError: 'Companies could not be loaded.',
    companiesLoadErrorDetail: 'An error occurred while loading companies.',
    accountsLoadError: 'Accounts could not be loaded.',
    accountsLoadErrorDetail: 'An error occurred while loading accounts.',
    selectCompanyFirst: 'Please select a company first.',
    seedLoadError: 'TDHP plan could not be loaded.',
    seedLoadErrorDetail: 'An error occurred while loading TDHP plan.',
    totalRecordsMessage: 'Total {{count}} records.',
    accountUpdateError: 'Account could not be updated.',
    accountCreateError: 'Account could not be created.',
    accountUpdated: 'Account updated.',
    accountCreated: 'New account added.',
    accountUpdateErrorDetail: 'An error occurred while updating account.',
    accountCreateErrorDetail: 'An error occurred while creating account.',
    deleteConfirm: 'Are you sure you want to delete this account?',
    accountDeleteError: 'Account could not be deleted.',
    accountDeleted: 'Account deleted.',
    accountDeleteErrorDetail: 'An error occurred while deleting account.',
    accountTypeActive: 'Asset',
    accountTypePassive: 'Liability',
    accountTypeIncome: 'Income',
    accountTypeExpense: 'Expense',
    accountTypeNa: 'N/A',
    accountKindMonetary: 'Monetary',
    accountKindMemorandum: 'Memorandum',
    accountKindOther: 'Other',
    balanceDebit: 'Debit',
    balanceCredit: 'Credit',
    readOnlyAccess: 'You do not have write access for this company.'
  }
};

const resolveLocaleKey = (value) => (String(value || '').toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR');

const initialFormState = {
  companyId: null,
  code: '',
  name: '',
  accountType: 1,
  accountKind: 1,
  balanceNature: 'B',
  levelNo: 1,
  currency: 'TL',
  postable: true,
  hasChildren: false,
  active: true,
  parentCode: '',
  parentId: ''
};

const buildHeaders = () => {
  const token = sessionStorage.getItem('authToken');
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

const getErrorMessage = (payload, fallback) => {
  if (payload instanceof Error) return payload.message;
  return fallback;
};

function TDHPPage() {
  const { language } = useLanguage();
  const localeKey = resolveLocaleKey(language);
  const text = UI_TEXT[localeKey];
  const t = useCallback((key) => text[key] || UI_TEXT['tr-TR'][key] || key, [text]);

  const ACCOUNT_TYPE_OPTIONS = useMemo(() => ([
    { value: 1, label: t('accountTypeActive') },
    { value: 2, label: t('accountTypePassive') },
    { value: 3, label: t('accountTypeIncome') },
    { value: 4, label: t('accountTypeExpense') },
    { value: 5, label: t('accountTypeNa') }
  ]), [t]);

  const ACCOUNT_KIND_OPTIONS = useMemo(() => ([
    { value: 1, label: t('accountKindMonetary') },
    { value: 2, label: t('accountKindMemorandum') },
    { value: 3, label: t('accountKindOther') }
  ]), [t]);

  const BALANCE_OPTIONS = useMemo(() => ([
    { value: 'B', label: t('balanceDebit') },
    { value: 'A', label: t('balanceCredit') }
  ]), [t]);

  const ACCOUNT_TYPE_LABELS = useMemo(() => ACCOUNT_TYPE_OPTIONS.reduce((acc, option) => ({ ...acc, [option.value]: option.label }), {}), [ACCOUNT_TYPE_OPTIONS]);
  const ACCOUNT_KIND_LABELS = useMemo(() => ACCOUNT_KIND_OPTIONS.reduce((acc, option) => ({ ...acc, [option.value]: option.label }), {}), [ACCOUNT_KIND_OPTIONS]);
  const BALANCE_LABELS = useMemo(() => BALANCE_OPTIONS.reduce((acc, option) => ({ ...acc, [option.value]: option.label }), {}), [BALANCE_OPTIONS]);

  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [seeding, setSeeding] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState('create');
  const [formState, setFormState] = useState(initialFormState);
  const [formError, setFormError] = useState('');
  const [editorLoading, setEditorLoading] = useState(false);
  const [editingAccountId, setEditingAccountId] = useState(null);
  const [deleteLoadingId, setDeleteLoadingId] = useState(null);
  const [seedReplace, setSeedReplace] = useState(false);

  const { canWrite } = useCompanyWriteAccess(selectedCompanyId);
  const companyName = useMemo(() => companies.find((company) => String(company.id) === selectedCompanyId)?.name, [companies, selectedCompanyId]);

  const buildEmptyForm = useCallback(() => ({
    ...initialFormState,
    companyId: selectedCompanyId ? Number(selectedCompanyId) : null
  }), [selectedCompanyId]);

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/companies`, { headers: buildHeaders() });
      if (!response.ok) throw new Error(t('companiesLoadError'));

      const data = await response.json();
      setCompanies(data);
      if (data.length > 0) setSelectedCompanyId(String(data[0].id));
    } catch (caught) {
      setErrorMessage(getErrorMessage(caught, t('companiesLoadErrorDetail')));
    }
  }, [t]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const fetchAccounts = useCallback(async () => {
    if (!selectedCompanyId) {
      setAccounts([]);
      return;
    }

    setLoading(true);
    setErrorMessage('');
    setStatusMessage('');

    try {
      const params = new URLSearchParams({ companyId: selectedCompanyId });
      if (searchQuery.trim()) params.append('search', searchQuery.trim());

      const response = await fetch(`${API_BASE_URL}/api/chart-of-accounts?${params.toString()}`, { headers: buildHeaders() });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || t('accountsLoadError'));
      }

      const data = await response.json();
      setAccounts(data);
    } catch (caught) {
      setErrorMessage(getErrorMessage(caught, t('accountsLoadErrorDetail')));
    } finally {
      setLoading(false);
    }
  }, [selectedCompanyId, searchQuery, t]);

  useEffect(() => {
    if (selectedCompanyId) fetchAccounts();
  }, [selectedCompanyId, fetchAccounts]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    fetchAccounts();
  };

  const handleSeed = async () => {
    if (!canWrite) {
      setErrorMessage(t('readOnlyAccess'));
      return;
    }
    if (!selectedCompanyId) {
      setErrorMessage(t('selectCompanyFirst'));
      return;
    }

    setSeeding(true);
    setErrorMessage('');
    setStatusMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/chart-of-accounts/seed/tdhp-kebir`, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify({ companyId: Number(selectedCompanyId), replace: seedReplace })
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || t('seedLoadError'));
      }

      const payload = await response.json();
      const totalText = t('totalRecordsMessage').replace('{{count}}', String(payload.inserted + payload.updated));
      setStatusMessage(`${payload.message} (${totalText})`);
      await fetchAccounts();
    } catch (caught) {
      setErrorMessage(getErrorMessage(caught, t('seedLoadErrorDetail')));
    } finally {
      setSeeding(false);
    }
  };

  const openCreateModal = () => {
    if (!canWrite) {
      setErrorMessage(t('readOnlyAccess'));
      return;
    }
    if (!selectedCompanyId) {
      setErrorMessage(t('selectCompanyFirst'));
      return;
    }
    setEditorMode('create');
    setEditingAccountId(null);
    setFormState(buildEmptyForm());
    setFormError('');
    setIsEditorOpen(true);
  };

  const openEditModal = (account) => {
    if (!canWrite) {
      setErrorMessage(t('readOnlyAccess'));
      return;
    }
    setEditorMode('edit');
    setEditingAccountId(account.id);
    setFormState({
      companyId: account.companyId,
      code: account.code,
      name: account.name,
      accountType: account.accountType,
      accountKind: account.accountKind,
      balanceNature: account.balanceNature,
      levelNo: account.levelNo ?? 1,
      currency: account.currency ?? 'TL',
      postable: !!account.postable,
      hasChildren: !!account.hasChildren,
      active: !!account.active,
      parentCode: account.parentCode ?? '',
      parentId: account.parentId ?? ''
    });
    setFormError('');
    setIsEditorOpen(true);
  };

  const closeModal = () => {
    setIsEditorOpen(false);
    setFormError('');
    setEditorLoading(false);
  };

  const buildPayload = () => ({
    companyId: Number(selectedCompanyId),
    code: formState.code.trim(),
    name: formState.name.trim(),
    accountType: Number(formState.accountType),
    accountKind: Number(formState.accountKind),
    balanceNature: formState.balanceNature,
    levelNo: formState.levelNo ? Number(formState.levelNo) : undefined,
    currency: formState.currency?.trim(),
    postable: formState.postable,
    hasChildren: formState.hasChildren,
    parentCode: formState.parentCode?.trim() || undefined,
    parentId: formState.parentId ? Number(formState.parentId) : undefined,
    active: formState.active
  });

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    if (!canWrite) {
      setFormError(t('readOnlyAccess'));
      return;
    }
    if (!selectedCompanyId) {
      setFormError(t('selectCompanyFirst'));
      return;
    }

    setEditorLoading(true);
    setFormError('');

    try {
      const payload = buildPayload();
      const url = editorMode === 'edit'
        ? `${API_BASE_URL}/api/chart-of-accounts/${editingAccountId}`
        : `${API_BASE_URL}/api/chart-of-accounts`;
      const response = await fetch(url, {
        method: editorMode === 'edit' ? 'PUT' : 'POST',
        headers: buildHeaders(),
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || (editorMode === 'edit' ? t('accountUpdateError') : t('accountCreateError')));
      }

      await response.json();
      setStatusMessage(editorMode === 'edit' ? t('accountUpdated') : t('accountCreated'));
      setIsEditorOpen(false);
      await fetchAccounts();
    } catch (caught) {
      setFormError(getErrorMessage(caught, editorMode === 'edit' ? t('accountUpdateErrorDetail') : t('accountCreateErrorDetail')));
    } finally {
      setEditorLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!canWrite) {
      setErrorMessage(t('readOnlyAccess'));
      return;
    }
    if (!window.confirm(t('deleteConfirm'))) return;

    setDeleteLoadingId(id);
    setErrorMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/chart-of-accounts/${id}?companyId=${selectedCompanyId}`, {
        method: 'DELETE',
        headers: buildHeaders()
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || t('accountDeleteError'));
      }

      setStatusMessage(t('accountDeleted'));
      await fetchAccounts();
    } catch (caught) {
      setErrorMessage(getErrorMessage(caught, t('accountDeleteErrorDetail')));
    } finally {
      setDeleteLoadingId(null);
    }
  };

  const handleFieldChange = (field) => (event) => {
    const { value } = event.target;
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = (field) => (event) => {
    setFormState((prev) => ({ ...prev, [field]: event.target.checked }));
  };

  return (
    <div className="card tdhp-card">
      <PageHeader
        title={t('title')}
        description={t('description')}
        actions={
          <div className="tdhp-header-actions">
            <label className="tdhp-replace-checkbox">
              <input type="checkbox" checked={seedReplace} onChange={(e) => setSeedReplace(e.target.checked)} />
              {t('replacePlan')}
            </label>
            <button type="button" className="btn-secondary" onClick={openCreateModal} disabled={!canWrite || !selectedCompanyId}>{t('addAccount')}</button>
            <button type="button" className="btn-primary" onClick={handleSeed} disabled={!canWrite || !selectedCompanyId || seeding}>
              {seeding ? t('loading') : t('loadPlan')}
            </button>
          </div>
        }
      />

      <div className="tdhp-toolbar">
        <div className="tdhp-control">
          <label htmlFor="tdhp-company-select">{t('company')}</label>
          <select id="tdhp-company-select" value={selectedCompanyId} onChange={(event) => setSelectedCompanyId(event.target.value)}>
            {companies.length === 0 ? (
              <option value="">{t('companyNotFound')}</option>
            ) : (
              companies.map((company) => (<option key={company.id} value={company.id}>{company.name}</option>))
            )}
          </select>
        </div>

        <form className="tdhp-control tdhp-search-form" onSubmit={handleSearchSubmit}>
          <label htmlFor="tdhp-search-input">{t('searchAccount')}</label>
          <div className="tdhp-search-row">
            <input id="tdhp-search-input" value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} placeholder={t('searchPlaceholder')} />
            <button type="submit" className="btn-secondary">{t('search')}</button>
          </div>
        </form>
      </div>

      <div className="table-wrapper">
        <table className="tdhp-table">
          <thead>
            <tr>
              <th>{t('code')}</th>
              <th>{t('name')}</th>
              <th>{t('type')}</th>
              <th>{t('kind')}</th>
              <th>{t('balanceNature')}</th>
              <th>{t('postable')}</th>
              <th>{t('status')}</th>
              <th className="tdhp-action-column">{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="table-placeholder">
                  <LoadingState message={t('loading')} />
                </td>
              </tr>
            ) : accounts.length === 0 ? (
              <tr>
                <td colSpan={8} className="table-placeholder">
                  <EmptyState title={!selectedCompanyId ? t('selectCompanyPrompt') : t('noAccounts')} />
                </td>
              </tr>
            ) : (
              accounts.map((account) => (
                <tr key={account.id}>
                  <td>{account.code}</td>
                  <td>
                    <strong>{account.name}</strong>
                    <p className="muted">{t('level')} {account.levelNo}</p>
                  </td>
                  <td>{ACCOUNT_TYPE_LABELS[account.accountType] || account.accountTypeLabel}</td>
                  <td>{ACCOUNT_KIND_LABELS[account.accountKind] || account.accountKindLabel}</td>
                  <td>{BALANCE_LABELS[account.balanceNature] || account.balanceNatureLabel}</td>
                  <td>{account.postable ? t('yes') : t('no')}</td>
                  <td><span className={account.active ? 'badge badge-active' : 'badge'}>{account.active ? t('active') : t('passive')}</span></td>
                  <td>
                    <div className="tdhp-table-actions">
                      <button type="button" className="action-icon-btn action-icon-btn-edit" onClick={() => openEditModal(account)} disabled={!canWrite} aria-label={t('edit')} title={t('edit')}>
                        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M3 17.25V21h3.75L18.81 8.94l-3.75-3.75L3 17.25Zm17.71-10.04a1.003 1.003 0 0 0 0-1.42L18.2 3.29a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 2.01-1.66Z" /></svg>
                      </button>
                      <button type="button" className="action-icon-btn action-icon-btn-delete" onClick={() => handleDelete(account.id)} disabled={!canWrite || deleteLoadingId === account.id} aria-label={t('delete')} title={t('delete')}>
                        {deleteLoadingId === account.id ? <span className="action-icon-loading">...</span> : <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 7h12l-1 14H7L6 7Zm3-3h6l1 2h4v2H4V6h4l1-2Z" /></svg>}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {errorMessage && <div className="alert alert-error">{errorMessage}</div>}
      {statusMessage && <div className="alert alert-success">{statusMessage}{companyName ? ` (${companyName})` : ''}</div>}

      <Modal
        isOpen={isEditorOpen}
        onClose={closeModal}
        title={editorMode === 'edit' ? t('editAccount') : t('newAccount')}
        width="680px"
      >
        <form className="tdhp-form" onSubmit={handleFormSubmit}>
          <div className="tdhp-form-grid">
            <label>{t('code')}<input name="code" value={formState.code} onChange={handleFieldChange('code')} required /></label>
            <label>{t('name')}<input name="name" value={formState.name} onChange={handleFieldChange('name')} required /></label>
            <label>{t('type')}<select name="accountType" value={formState.accountType} onChange={handleFieldChange('accountType')}>{ACCOUNT_TYPE_OPTIONS.map((option) => (<option key={option.value} value={option.value}>{option.label}</option>))}</select></label>
            <label>{t('kind')}<select name="accountKind" value={formState.accountKind} onChange={handleFieldChange('accountKind')}>{ACCOUNT_KIND_OPTIONS.map((option) => (<option key={option.value} value={option.value}>{option.label}</option>))}</select></label>
            <label>{t('balanceNature')}<select name="balanceNature" value={formState.balanceNature} onChange={handleFieldChange('balanceNature')}>{BALANCE_OPTIONS.map((option) => (<option key={option.value} value={option.value}>{option.label}</option>))}</select></label>
            <label>{t('levelNo')}<input type="number" min="1" name="levelNo" value={formState.levelNo} onChange={handleFieldChange('levelNo')} /></label>
            <label>{t('currency')}<input name="currency" value={formState.currency} onChange={handleFieldChange('currency')} maxLength={3} /></label>
            <label>{t('parentCode')}<input name="parentCode" value={formState.parentCode} onChange={handleFieldChange('parentCode')} /></label>
            <label>{t('parentId')}<input type="number" min="1" name="parentId" value={formState.parentId} onChange={handleFieldChange('parentId')} /></label>
            <div className="tdhp-checkbox-row">
              <label><input type="checkbox" checked={formState.postable} onChange={handleCheckboxChange('postable')} />{t('postable')}</label>
              <label><input type="checkbox" checked={formState.hasChildren} onChange={handleCheckboxChange('hasChildren')} />{t('hasChildren')}</label>
              <label><input type="checkbox" checked={formState.active} onChange={handleCheckboxChange('active')} />{t('active')}</label>
            </div>
          </div>

          {formError && <div className="alert alert-error">{formError}</div>}

          <div className="tdhp-form-footer">
            <button type="button" className="btn-secondary" onClick={closeModal} disabled={editorLoading}>{t('cancel')}</button>
            <button type="submit" className="btn-primary" disabled={editorLoading}>{editorLoading ? t('saving') : editorMode === 'edit' ? t('update') : t('save')}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default TDHPPage;
